import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Menu, X } from "lucide-react";

export function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const poll = () => api.get("/conversations").then((r) => { if (alive) setUnread(r.data.unread_total || 0); }).catch(() => {});
    poll();
    const t = setInterval(poll, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [user, location.pathname]);

  const mNavLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`block py-3.5 font-mono text-sm uppercase tracking-[0.18em] transition-colors ${
        location.pathname === to ? "text-gold" : "text-ash active:text-cream"
      }`}
    >
      {label}
    </Link>
  );

  const navLink = (to, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      onClick={() => setOpen(false)}
      className={`font-mono text-xs uppercase tracking-[0.18em] transition-colors ${
        location.pathname === to ? "text-gold" : "text-ash hover:text-cream"
      }`}
    >
      {label}
    </Link>
  );

  return (
    <header className="fixed top-0 left-0 w-full z-50 bg-ink/85 backdrop-blur-xl border-b border-line">
      <div className="max-w-[1400px] mx-auto flex justify-between items-center px-5 md:px-10 h-16">
        <Link to="/" data-testid="logo-home-link" className="flex items-center text-cream">
          <Wordmark height={36} />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {user ? (
            <>
              {navLink("/directory", "The Haus", "nav-directory")}
              {navLink("/radio", "Radio", "nav-radio")}
              {navLink("/profile", "My HausCard", "nav-profile")}
              {navLink("/saved", "Saved", "nav-saved")}
              <Link to="/messages" data-testid="nav-messages" onClick={() => setOpen(false)} className={`relative font-mono text-xs uppercase tracking-[0.18em] transition-colors ${location.pathname === "/messages" ? "text-gold" : "text-ash hover:text-cream"}`}>
                Messages
                {unread > 0 && <span data-testid="unread-badge" className="absolute -top-2.5 -right-4 min-w-[17px] h-[17px] px-1 bg-red-600 text-white font-bold text-[10px] rounded-full flex items-center justify-center">{unread > 9 ? "9+" : unread}</span>}
              </Link>
              {navLink("/briefs", "Briefs", "nav-briefs")}
              {user.is_admin && navLink("/admin", "Curate", "nav-admin")}
              <div className="flex items-center gap-3 pl-4 border-l border-line">
                <span className="font-mono text-xs text-ash">{user.name?.split(" ")[0]}</span>
                <button
                  data-testid="logout-btn"
                  onClick={() => { logout(); navigate("/"); }}
                  className="font-mono text-xs uppercase tracking-[0.18em] text-ash hover:text-gold transition-colors"
                >
                  Exit
                </button>
              </div>
            </>
          ) : (
            <>
              <a href="/#how" className="font-mono text-xs uppercase tracking-[0.18em] text-ash hover:text-cream transition-colors">How it works</a>
              <Link
                to="/auth"
                data-testid="header-enter-btn"
                className="bg-gold text-ink font-bold uppercase tracking-widest text-xs px-6 py-3 hover:bg-gold-hi transition-colors"
              >
                Enter the Haus
              </Link>
            </>
          )}
        </nav>

        <button className="md:hidden -mr-2 p-2 text-cream" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-line bg-ink px-5 py-4 flex flex-col divide-y divide-line/50">
          {user ? (
            <>
              {mNavLink("/directory", "The Haus", "m-nav-directory")}
              {mNavLink("/radio", "Radio", "m-nav-radio")}
              {mNavLink("/profile", "My HausCard", "m-nav-profile")}
              {mNavLink("/saved", "Saved", "m-nav-saved")}
              <Link to="/messages" data-testid="m-nav-messages" onClick={() => setOpen(false)} className={`block py-3.5 font-mono text-sm uppercase tracking-[0.18em] transition-colors ${location.pathname === "/messages" ? "text-gold" : "text-ash active:text-cream"}`}>
                Messages
                {unread > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full align-middle">{unread > 9 ? "9+" : unread}</span>}
              </Link>
              {mNavLink("/briefs", "Briefs", "m-nav-briefs")}
              {user.is_admin && mNavLink("/admin", "Curate", "m-nav-admin")}
              <button
                data-testid="m-logout-btn"
                onClick={() => { logout(); setOpen(false); navigate("/"); }}
                className="text-left py-3.5 font-mono text-sm uppercase tracking-[0.18em] text-ash active:text-gold"
              >
                Exit
              </button>
            </>
          ) : (
            <>
              <a href="/#how" onClick={() => setOpen(false)} className="block py-3.5 font-mono text-sm uppercase tracking-[0.18em] text-ash">How it works</a>
              <Link to="/auth" onClick={() => setOpen(false)} data-testid="m-header-enter-btn" className="bg-gold text-ink font-bold uppercase tracking-widest text-sm px-6 py-4 text-center mt-2">
                Enter the Haus
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
